// @migration-in-progress
import React, { useEffect } from 'react';
import TailwindDashboard from 'modules/dashboard';
import CrewModule from 'modules/crew';
import OperationsModule from 'modules/operations';
import MaintenanceModule from 'modules/maintenance';
import DrillsModule from 'modules/drills';
import DefectsModule from './modules/defects/DefectsModule';
import LogbookModule from 'modules/logbook';
import { RiskAssessmentModule } from '../../modules/risk-assessment';
import IncidentsModule from 'modules/incidents';
import SMSDocumentsModule from './modules/SMSDocumentsModule';
import PoliciesModule from './modules/PoliciesModule';  // FT-122: Docs & Compliance with Compliance Register tab
import ReportsModule from 'modules/reports';
import DataManagement from './DataManagement';
import AccountManagement from './AccountManagement';
import VesselConfigDemo from './modules/crew/VesselConfigDemo';
// TestWizardHarness archived - no longer needed
import RegulatoryImport from './RegulatoryImport';
import UIPlaygroundModule from '../../modules/ui-playground/UIPlaygroundModule';
import DesignLabModule from '../../modules/ui-playground/design-lab/DesignLabModule';
import { FleetDashboard } from './modules/fleet';
import FleetLanding from './modules/fleet/FleetLanding';  // FT-436 (VESSELS-ONLY): photo-card fleet landing
import AdminPanel from './admin/AdminPanel';
import HelpCenterModule from './modules/help/HelpCenterModule';
import ClientAdminPanel from './client-admin/ClientAdminPanel';
import { FLAGS } from '../../config/featureFlags';

// Placeholder component for modules
const ModulePlaceholder = ({ title, items }) => (
  <div>
    <div className="mb-8">
      <h1 className="text-2xl font-bold text-gray-900">{title}</h1>
      <p className="mt-1 text-sm text-gray-500">Module functionality coming soon</p>
    </div>
    
    <div className="bg-white shadow rounded-lg p-6">
      <h3 className="text-lg font-medium text-gray-900 mb-4">Available Sections:</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {items.map((item, index) => (
          <div key={index} className="flex items-center p-3 border border-gray-200 rounded-lg">
            <span className="text-sm text-gray-700">{item}</span>
          </div>
        ))}
      </div>
    </div>
  </div>
);

const TailwindMainContent = ({ activeModule, setActiveModule, setActiveModuleWithTab, onOpenVesselSettings, onSelectFleetVessel, voyageFullScreen, setVoyageFullScreen }) => {
  // activeModule and navigation functions are now passed as props from TailwindShell

  // FT-138: Scroll to top when switching modules
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [activeModule]);

  // Reset voyageFullScreen when leaving operations module
  // This prevents the full-screen layout from persisting to other modules
  useEffect(() => {
    if (activeModule !== 'operations' && voyageFullScreen) {
      setVoyageFullScreen(false);
    }
  }, [activeModule, voyageFullScreen, setVoyageFullScreen]);

  const renderContent = () => {
    switch (activeModule) {
      case 'dashboard':
        return <TailwindDashboard 
          setActiveModule={setActiveModule}
          setActiveModuleWithTab={setActiveModuleWithTab}
          onOpenVesselSettings={onOpenVesselSettings} 
        />;
      case 'policies':
        // FT-354a: pass setActiveModule so mobile dead-end screens can back-navigate.
        return <PoliciesModule setActiveModule={setActiveModule} />;  // FT-122: Docs & Compliance with Compliance Register tab
      case 'risk':
        // FT-357: pass setActiveModule so RiskAssessmentModule can register a
        // "back to dashboard" handler with the shell.
        return <RiskAssessmentModule setActiveModule={setActiveModule} />;
      case 'operations':
        return <OperationsModule
          setActiveModule={setActiveModule}
          voyageFullScreen={voyageFullScreen}
          setVoyageFullScreen={setVoyageFullScreen}
        />;
      case 'logbook':
        // FT-356: pass setActiveModule so the top-level logbook view can
        // register a "back to dashboard" handler with the shell.
        return <LogbookModule setActiveModule={setActiveModule} />;
      case 'maintenance':
        return <MaintenanceModule
          setActiveModule={setActiveModule}
          setActiveModuleWithTab={setActiveModuleWithTab}
          setShowVesselSettingsView={onOpenVesselSettings}
        />;
      case 'drills':
        // FT-356: pass setActiveModule so drill dead-end views can register
        // a back handler with the shell.
        return <DrillsModule setActiveModule={setActiveModule} />;
      case 'defects':
        return <DefectsModule 
          setActiveModule={setActiveModule}
          setActiveModuleWithTab={setActiveModuleWithTab}
        />;
      case 'incidents':
        // FT-357: pass setActiveModule so IncidentsModule can register a
        // "back to dashboard" handler with the shell.
        return <IncidentsModule setActiveModule={setActiveModule} />;
      case 'crew':
        // FT-357: pass setActiveModule so CrewModule can register a
        // "back to dashboard" handler with the shell.
        return <CrewModule setActiveModule={setActiveModule} />;
      case 'reports':
        // FT-354a: pass setActiveModule so mobile dead-end screens can back-navigate.
        return <ReportsModule setActiveModule={setActiveModule} />;
      case 'data-management':
        return <DataManagement />;
      case 'account':
        return <AccountManagement />;
      case 'fleet':
        return <FleetDashboard />;
      case 'fleet-landing':
        // FT-436 (VESSELS-ONLY): photo-card landing. Tapping a card enters that
        // vessel's lane (setCurrentVessel → dashboard), exactly as VesselSelector.
        return (
          <FleetLanding
            onSelectVessel={onSelectFleetVessel}
            onOpenVesselSettings={onOpenVesselSettings ? () => onOpenVesselSettings() : undefined}
          />
        );
      case 'admin':
        return <AdminPanel />;
      case 'client-admin':
        return <ClientAdminPanel />;
      case 'vessel-config-demo':
        return <VesselConfigDemo />;
      case 'test-wizard':
        return <div className="p-8 text-gray-500">Test Wizard removed - use vessel settings instead</div>;
      case 'regulatory-import':
        return (
          <div className="max-w-4xl mx-auto">
            <div className="mb-8">
              <h1 className="text-2xl font-bold text-gray-900">Regulatory Requirements Import</h1>
              <p className="mt-1 text-sm text-gray-500">Import vessel requirements from the regulatory calculator</p>
            </div>
            <RegulatoryImport onImportComplete={(results) => {

              // Optionally redirect to relevant modules after import
            }} />
          </div>
        );
      case 'ui-playground':
        // Only render if feature flag is enabled (DEV-ONLY)
        return FLAGS.UI_PLAYGROUND ? <UIPlaygroundModule /> : <TailwindDashboard />;
      case 'design-lab':
        // Only render if feature flag is enabled (DEV-ONLY)
        return FLAGS.DESIGN_LAB ? <DesignLabModule /> : <TailwindDashboard />;
      case 'help':
        return <HelpCenterModule setActiveModule={setActiveModule} />;
      default:
        return <TailwindDashboard />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {renderContent()}
    </div>
  );
};

export default TailwindMainContent;